import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { authApi } from "../../services/api/auth.api";

export const AuthApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [loginDialog, setLoginDialog] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults((prev) => ({
        ...prev,
        [name]: { success: true, data: result },
      }));
    } catch (err: any) {
      setResults((prev) => ({
        ...prev,
        [name]: {
          success: false,
          error: err.message || "Неизвестная ошибка",
        },
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Основные endpoints аутентификации
  const testGetMe = () => testEndpoint("Get Me", () => authApi.getMe());
  const testLogout = () => testEndpoint("Logout", () => authApi.logout());
  const testForgotPassword = () =>
    testEndpoint("Forgot Password", () =>
      authApi.forgotPassword("test@example.com"),
    );

  // MFA endpoints (ADR-015: auth/mfa)
  const testSetupMfa = () =>
    testEndpoint("Setup MFA", () => authApi.setupMfa());
  const testDisableMfa = () =>
    testEndpoint("Disable MFA", () => authApi.disableMfa());
  const testConfirmMfa = () =>
    testEndpoint("Confirm MFA", () => authApi.confirmMfa("123456"));
  const testMfaStatus = () =>
    testEndpoint("MFA Status", () => authApi.getMfaStatus());

  // Управление сессиями
  const testGetSessions = () =>
    testEndpoint("Get Sessions", () => authApi.getSessions());
  const testLogoutAll = () =>
    testEndpoint("Logout All", () => authApi.logoutAll());

  // Тест логина с диалогом
  const testLogin = () => {
    if (!loginData.email || !loginData.password) {
      setError("Введите email и пароль");
      return;
    }
    testEndpoint("Login", () => authApi.login(loginData));
    setLoginDialog(false);
  };

  const testAllEndpoints = async () => {
    await testGetMe();
    await testGetSessions();
    await testMfaStatus();
    await testGetSessions();
  };

  const clearResults = () => {
    setResults({});
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔐 Тестирование Auth API
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование 15 endpoints из Auth модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button
            variant="contained"
            onClick={testAllEndpoints}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Тестировать все endpoints
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => setLoginDialog(true)}
            disabled={loading}
          >
            Тест Login
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={testGetMe} disabled={loading}>
            Тест Get Me
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={testLogout} disabled={loading}>
            Тест Logout
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={testSetupMfa} disabled={loading}>
            Тест MFA Setup
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={testGetSessions}
            disabled={loading}
          >
            Тест Sessions
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={testForgotPassword}
            disabled={loading}
          >
            Forgot Password
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={testDisableMfa}
            disabled={loading}
          >
            Disable MFA
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={testConfirmMfa}
            disabled={loading}
          >
            Confirm MFA
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={testLogoutAll} disabled={loading}>
            Logout All
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={clearResults} disabled={loading}>
            Очистить результаты
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {Object.entries(results).map(([name, result]) => (
          <Grid item xs={12} md={6} key={name}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {name}
                </Typography>
                {result.success ? (
                  <Alert severity="success">
                    ✅ Успешно
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {JSON.stringify(result.data, null, 2)}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error">❌ Ошибка: {result.error}</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {Object.keys(results).length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Нажмите кнопку для начала тестирования
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Диалог для логина */}
      <Dialog open={loginDialog} onClose={() => setLoginDialog(false)}>
        <DialogTitle>Тест Login</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={loginData.email}
            onChange={(e) =>
              setLoginData((prev) => ({ ...prev, email: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={loginData.password}
            onChange={(e) =>
              setLoginData((prev) => ({ ...prev, password: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginDialog(false)}>Отмена</Button>
          <Button onClick={testLogin} variant="contained">
            Тестировать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuthApiTest;
